/* WorkTriggerInstanceEditor.js */

"use strict";
const GameEditor = require("@gluenetworks/game_editor");
const _ = require("lodash");

class WorkTriggerInstanceEditor extends GameEditor {
    constructor(
        logger,
        di,
        registeredDetails,
        editorState,
        editorLayoutMgr,
        editorLayout,
        TriggerTypeEntity,
        TriggerNotifType,
        UserEntity,
        DeviceEntity,
        OsUpgradePlanEntity,
        ConstructTypeEntity,
        ConstructInstanceEntity,
        userSession,
        editorTabCmpnt,
        solutionsManager
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._registeredDetails = registeredDetails;
        this._editorLayout = editorLayout;
        this._tabCmpnt = editorTabCmpnt;

        this._userSession = userSession;
        this._activeOrgId = userSession.activeOrganization.id;

        let solutionDefinition = solutionsManager.getActiveSolution();
        let triggerTypeEditorViews = _.get(solutionDefinition, "workTriggerEditor.viewNamespaces", []);
        let triggerTypeEditorActions = _.get(solutionDefinition, "workTriggerEditor.actionNamespaces", []);

        this.setNamespaceGlobs(triggerTypeEditorViews.concat(triggerTypeEditorActions), true);

        this._TriggerTypeEntity = TriggerTypeEntity;
        this._TriggerNotifType = TriggerNotifType;
        this._OsUpgradePlanEntity = OsUpgradePlanEntity;
        this._UserEntity = UserEntity;
        this._DeviceEntity = DeviceEntity;
        this._ConstructTypeEntity = ConstructTypeEntity;
        this._ConstructInstanceEntity = ConstructInstanceEntity;

        this._triggerTypeProm = undefined;
        this._triggerType = editorState.launchOptions.triggerType;
        this._viewSettings = editorState.launchOptions;

        this._triggerTypeId = editorState.id;
        this._triggerTypeName = editorState.launchOptions.title || "Unknown";
        this._triggerTypeTypeIcon = "worktrigger.schedule";
        this._instanceEditorState = {};
        this.currentWorkTrigRemoved = false;
        this.currentWorkTrigDeleteInProgress = false;

        this.retardActions(true);

        this._initTriggerType();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListenersOnWorkTrigger() {
        this._triggerType.on("DisplayNamePropertyUpdated", (this._onDisplayNamePropertyUpdated = this._onDisplayNamePropertyUpdated.bind(this)));
        this._triggerType.on(
            "DetectedTypeNamePropertyUpdated",
            (this._onDetectedTypeNamePropertyUpdated = this._onDetectedTypeNamePropertyUpdated.bind(this))
        );
        this._triggerType.on("EntityDeletedFromSystem", (this._onWorkTriggerRemoved = this._onWorkTriggerRemoved.bind(this)));
        this._triggerType.on("DirtyStateChanged", (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this)));
        this._triggerType.on("PropertiesUpdated", (this._onPropertiesUpdated = this._onPropertiesUpdated.bind(this)));
    }

    _unregisterListenersOnWorkTrigger() {
        this._triggerType.removeListener("DisplayNamePropertyUpdated", this._onDisplayNamePropertyUpdated);
        this._triggerType.removeListener("DetectedTypeNamePropertyUpdated", this._onDetectedTypeNamePropertyUpdated);
        this._triggerType.removeListener("EntityDeletedFromSystem", this._onWorkTriggerRemoved);
        this._triggerType.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._triggerType.removeListener("PropertiesUpdated", this._onPropertiesUpdated);
    }

    _onDirtyStateChanged(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    _onDisplayNamePropertyUpdated() {
        this.updateTab(this._triggerType.pristineProperties.displayName);
    }

    _onDetectedTypeNamePropertyUpdated() {
        this.updateTabIcon(
            (this._triggerTypeTypeIcons[this._triggerType.pristineProperties.detectedTypeName] &&
                this._triggerTypeTypeIcons[this._triggerType.pristineProperties.detectedTypeName].icon) ||
                this._triggerTypeTypeIcons.unknown.icon
        );
    }

    _onWorkTriggerRemoved() {
        this.currentWorkTrigRemoved = true;
        if (!this.currentWorkTrigDeleteInProgress) {
            this.currentWorkTrigRemoved = false; // resetting to false on closing current worktrig editor
            this.closeEditor(this.editorTabStateId, true);
        }
    }

    _initTriggerType() {
        let fields = { updatedIp: false }; //We want to limit some properties that are unhandled and may cause update issues
        this._triggerTypeProm = this._TriggerTypeEntity
            .retrieve(this._activeOrgId, "TriggerType", this._triggerTypeId, fields)
            .then((triggerType) => {
                this._triggerType = triggerType;
                this._registerListenersOnWorkTrigger();

                this._triggerTypeName = this._triggerType.name;
                this._tabCmpnt.setEditorIconName(this._triggerTypeTypeIcon);
                this._tabCmpnt.setTitle(this._triggerTypeName);
            });
    }

    _onPropertiesUpdated(/*entity_properties, propertiesBeforeRebase, entity_pendingPatches*/) {
        // console.log(entity_properties, propertiesBeforeRebase, entity_pendingPatches);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get editorTabStateId() {
        return _.get(this.getViewSettings(), "id");
    }

    getViewSettings() {
        return this._viewSettings;
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getTriggerType() {
        return this._triggerType;
    }

    whenTriggerTypeIsReady() {
        return this._triggerTypeProm;
    }

    getWorkType() {
        return _.get(this._triggerType, "workTypeDef");
    }

    whenWorkTypeIsReady() {
        return Promise.resolve();
    }

    getOsUpgradePlanEntity() {
        return this._OsUpgradePlanEntity;
    }

    getControlUsers() {
        return this._UserEntity.loadAllUsers();
    }

    getConstructTypes(construct) {
        return this._ConstructTypeEntity.loadTypes(this._activeOrgId, construct);
    }

    getConstructInstances(construct, typeId) {
        return this._ConstructInstanceEntity.retrieveInstances(this._activeOrgId, construct, `typeId = "${typeId}"`, { name: 1 });
    }

    getConstructInstancesForTypeName(construct, typeName) {
        return this._ConstructInstanceEntity.retrieveInstances(this._activeOrgId, construct, `typeName = "${typeName}"`, { name: 1 });
    }

    getUserSession() {
        return this._userSession;
    }

    getDeviceEntityClass() {
        return this._DeviceEntity;
    }
    getConstructInstanceEntityClass() {
        return this._ConstructInstanceEntity;
    }
    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    shutdown() {
        this._unregisterListenersOnWorkTrigger();
        let logger = this._logger;

        return super
            .shutdown()
            .then(() => {
                return this._triggerType.shutdown();
            })
            .then(() => {
                this._tabCmpnt = undefined;
                this._triggerType = undefined;
                this._triggerTypeProm = undefined;
                this._registeredDetails = undefined;
                this._editorLayout = undefined;
                this._activeOrgId = undefined;
                this._triggerType = undefined;
                this._triggerTypeId = undefined;
                this._triggerTypeName = undefined;
                this.currentWorkTrigRemoved = undefined;
                this.currentWorkTrigDeleteInProgress = undefined;
            })
            .catch((err) => {
                logger.warn({ error: err }, "WorkTriggerInstanceEditor.shutdown() error");
            });
    }

    loadWorkTypes() {
        return {};
    }

    loadTriggerNotifTypes() {
        return this._TriggerNotifType.loadAllTriggerNotifTypes(this._activeOrgId);
    }

    setInstanceEditorStateProperty(name, val) {
        _.set(this._instanceEditorState, name, val);
    }

    getInstanceEditorStateProperty(name) {
        return _.get(this._instanceEditorState, name);
    }
}

module.exports = WorkTriggerInstanceEditor;

WorkTriggerInstanceEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "$teve.registeredDetails",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "EditorLayout.EditorLayout",
    "$teve.entities.TriggerType",
    "$teve.entities.TriggerNotifType",
    "$teve.entities.User",
    "$teve.entities.Device",
    "$teve.entities.OsUpgradePlan",
    "$teve.entities.ConstructType",
    "$teve.entities.ConstructInstance",
    "$teve.userSession",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager"
];

//Optional convenient way to set a tab icon.
WorkTriggerInstanceEditor.$editorIcon = "common.default_device";
WorkTriggerInstanceEditor.$editorIconType = "base-icon-gl";
